import clsx from 'clsx'
import { useParams, useNavigate } from 'react-router-dom'
import { useState, useEffect, useContext } from 'react'
import { approveSubmittal, rejectSubmittal } from '@/services/submittalService'
import EFormRenderer from '@/components/EForm/EformRenderer'
import Loader from '@/components/Loader'
import AccordionCard from '@/components/AccordionCard'
import Button from '@/components/Button'
import ChevronLeft from '@/assets/svgs/chevron-left.svg?react'
import InfoIcon from '@/assets/svgs/information-circle.svg?react'
import { useSubmittalDetail } from '@/hooks/useSubmittalDetail'
import useModal from '@/hooks/useModal'
import { SubmittalHeader } from './components/SubmittalHeader'
import { SubmittalBanner } from './components/SubmittalBanner'
import { ApproveModal } from './components/ApproveModal'
import { RejectModal } from './components/RejectModal'
import { ToastContext } from '@/context/ToastContext'
import { SUBMITTAL_STATUS } from './constants'
import ActivityLogModal from '@/components/ActivityLogModal'
import { buildLogEntries } from './utils/buildLogEntries'

export const SubmittalDetailPage = () => {
  const { submittalId } = useParams()
  const navigate = useNavigate()
  const { addToastMessage } = useContext(ToastContext)
  const { state, activityLogs, refetch } = useSubmittalDetail(submittalId)
  const [actionState, setActionState] = useState({ submitting: false })
  const [rejectReason, setRejectReason] = useState('')
  const {
    visible: approveModalVisible,
    show: showApproveModal,
    hide: hideApproveModal,
  } = useModal()
  const { visible: rejectModalVisible, show: showRejectModal, hide: hideRejectModal } = useModal()
  const { visible: activityLogVisible, show: showActivityLog, hide: hideActivityLog } = useModal()

  const handleApprove = async () => {
    hideApproveModal()
    setActionState({ submitting: true })
    try {
      await approveSubmittal(submittalId)
      addToastMessage(
        `E-Form submission approved and shared to ${state.receiverOrgName}`,
        'success',
        'dismiss'
      )
    } catch (err) {
      addToastMessage(err.response?.data?.detail || err.message, 'error', 'dismiss')
    } finally {
      refetch()
      setActionState({ submitting: false })
    }
  }

  const handleReject = async () => {
    hideRejectModal()
    setActionState({ submitting: true })
    try {
      await rejectSubmittal(submittalId, rejectReason)
      addToastMessage('E-Form submission rejected successfully.', 'error', 'dismiss')
    } catch (err) {
      addToastMessage(err.response?.data?.detail || err.message, 'error', 'dismiss')
    } finally {
      refetch()
      setActionState({ submitting: false })
    }
  }

  const handleOpenReject = () => {
    setRejectReason('')
    showRejectModal()
  }

  useEffect(() => {
    if (state.notFound) {
      addToastMessage(state.error || 'Submittal could not be found', 'error', 'dismiss')
      navigate('/submittals', { replace: true })
    }
  }, [state.notFound])

  const isLoading = !state.error && (state.loading || !state.schema)
  const latestLog = activityLogs[0] ?? null

  return (
    <div>
      {!isLoading && !state.error && (
        <SubmittalHeader
          elementName={state.elementName}
          status={state.status}
          projectContractName={state.projectContractName}
          submissionMonth={state.submissionMonth}
          modified={state.modified}
        />
      )}
      <div
        className={
          state.status !== SUBMITTAL_STATUS.PENDING_REVIEW ? 'sm:pb-24 pb-10' : 'sm:pb-35 pb-42'
        }
      >
        {isLoading && (
          <div className="flex items-center justify-center bg-white rounded-lg py-6 px-8 min-h-[calc(100vh-8rem)]">
            <Loader />
          </div>
        )}
        {!isLoading && state.error && (
          <div className="flex items-center justify-center bg-white rounded-lg py-6 px-8 min-h-[25rem]">
            <div className="flex flex-col items-center gap-4 text-center">
              <p className="type-body-2 text-grey-50">{state.error}</p>
              <Button sizing="Medium" round onClick={refetch}>
                Retry
              </Button>
            </div>
          </div>
        )}
        {!isLoading && !state.error && (
          <>
            <SubmittalBanner
              status={state.status}
              receiverOrgName={state.receiverOrgName}
              latestLog={latestLog}
              onViewActivityLog={activityLogs.length > 0 ? showActivityLog : null}
            />
            <AccordionCard
              className="mt-6"
              contentClassName="max-h-[10000px]"
              title={
                <div data-testid="eform-page-form-title" className="type-header-4 font-[900]">
                  {state?.elementName || 'Data'} Details
                </div>
              }
              defaultOpen
            >
              <div className="flex flex-wrap items-start justify-between gap-y-6 type-body-2">
                <EFormRenderer schema={state.schema} payload={state.payload} />
              </div>
            </AccordionCard>
            <div className="z-[1] flex flex-col fixed bottom-0 left-0 w-full ml-0 tablet-p:w-[calc(100%-15rem)] tablet-p:ml-60 border-t border-grey-90 bg-white">
              {state.status === SUBMITTAL_STATUS.PENDING_REVIEW && (
                <div className="flex items-center gap-2 py-4 px-8 max-phone:px-4 border-b border-grey-90">
                  <InfoIcon className="shrink-0 w-4 h-4 text-grey-50" />
                  <span className="font-avenir-medium type-body-2 text-grey-40">
                    By clicking &ldquo;Approve&rdquo;, you consent to the automated sharing of this
                    data element to the intended recipient.
                  </span>
                </div>
              )}
              <div
                className={clsx(
                  'flex pt-5 px-8 pb-4 max-phone:flex-wrap max-phone:gap-2 max-phone:py-3 max-phone:px-4',
                  state.status !== SUBMITTAL_STATUS.PENDING_REVIEW && 'max-phone:hidden'
                )}
              >
                <div className="hidden md:flex items-center">
                  <Button
                    data-testid="submittal-view-other-btn"
                    noborder
                    sizing="Medium"
                    disabled={actionState.submitting}
                    onClick={() => navigate('/submittals')}
                  >
                    <ChevronLeft />
                    View Other Submissions
                  </Button>
                </div>
                {state.status === SUBMITTAL_STATUS.PENDING_REVIEW && (
                  <div className="flex items-center gap-3 ml-auto max-phone:ml-0 max-phone:w-full max-phone:justify-between">
                    <Button
                      data-testid="submittal-reject-btn"
                      sizing="Medium"
                      round
                      error
                      disabled={
                        actionState.submitting || state.status !== SUBMITTAL_STATUS.PENDING_REVIEW
                      }
                      onClick={handleOpenReject}
                      addClass="md:min-w-[160px] md:max-w-[160px] w-full"
                    >
                      Reject
                    </Button>
                    <Button
                      data-testid="submittal-approve-btn"
                      sizing="Medium"
                      round
                      disabled={
                        actionState.submitting || state.status !== SUBMITTAL_STATUS.PENDING_REVIEW
                      }
                      onClick={showApproveModal}
                      addClass="md:min-w-[160px] md:max-w-[160px] w-full"
                    >
                      Approve
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <ApproveModal
        visible={approveModalVisible}
        onClose={hideApproveModal}
        onConfirm={handleApprove}
      />
      <RejectModal
        visible={rejectModalVisible}
        onClose={hideRejectModal}
        onConfirm={handleReject}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
      />

      <ActivityLogModal
        visible={activityLogVisible}
        close={hideActivityLog}
        entries={buildLogEntries(activityLogs)}
      />
    </div>
  )
}
